import { Component, Show } from "solid-js";
import type {
  AutoPrioritizeBatchProgress,
  AutoPrioritizeStage,
} from "../../api";

interface AutoPrioritizeProgressBarProps {
  /** Which pass is in flight (FLOW-946). */
  stage: AutoPrioritizeStage;
  /** Items done / to do — only the two batched passes have this. */
  progress?: AutoPrioritizeBatchProgress;
}

/**
 * One line per pass. `focus_detection` is the client's own `detecting` state:
 * the backend cannot report it, because detection finishes before there is a
 * job to poll.
 */
const LABEL: Record<AutoPrioritizeStage, string> = {
  focus_detection: "Detecting the current focus…",
  preparing: "Reading the backlog…",
  relevance_filter: "Filtering for relevance",
  sizing: "Estimating sizes",
  scoring: "Scoring and ranking…",
};

export const AutoPrioritizeProgressBar: Component<
  AutoPrioritizeProgressBarProps
> = (props) => {
  // A stage this build does not know reads as the generic line, never as
  // `undefined` in the DOM.
  const label = () => LABEL[props.stage] ?? "Prioritizing…";
  // `total === 0` is the backend's "no per-item work" and `pct`'s
  // divide-by-zero guard.
  const counted = () => {
    const p = props.progress;
    return p && p.total > 0 ? p : null;
  };
  // Clamped here rather than trusting the poll: `aria-valuenow` must never
  // exceed `aria-valuemax`, nor the fill overrun its track.
  const pct = () => {
    const p = counted();
    return p ? Math.min(100, Math.round((p.done / p.total) * 100)) : 0;
  };

  return (
    <div class="sticky top-0 z-10 flex flex-col gap-1.5 px-6 py-2 bg-signal-magenta-tint border-b border-signal-magenta/25 text-ink">
      <div class="flex items-center gap-1.5 text-sm">
        <span class="font-mono text-[9px] uppercase tracking-[0.12em] px-1 rounded-sm border border-signal-magenta/25 text-signal-magenta">
          ai
        </span>{" "}
        {/* `role="status"` wraps the sentence only: the counter changes on
            every poll, and announcing it each time would talk over the user.
            The progressbar's `aria-valuetext` carries it on demand instead. */}
        <span role="status" class="font-medium">
          {label()}
        </span>
        <Show when={counted()}>
          {(p) => (
            <span
              aria-hidden="true"
              class="font-mono tabular-nums text-xs text-ink-2"
            >
              {p().done}/{p().total} items
            </span>
          )}
        </Show>
      </div>
      <Show when={counted()}>
        {(p) => (
          <div
            role="progressbar"
            aria-label={label()}
            aria-valuemin="0"
            aria-valuemax="100"
            aria-valuenow={pct()}
            aria-valuetext={`${p().done} of ${p().total} items`}
            class="h-1.5 w-full rounded-full bg-line overflow-hidden"
          >
            <div
              class="h-full rounded-full bg-signal-magenta"
              style={{ width: `${pct()}%` }}
            />
          </div>
        )}
      </Show>
    </div>
  );
};
